import * as React from 'react';

import _ from 'underscore';

import * as Schema from 'nutshell-core/schema';

import {FormField} from 'shells/form/form-field';
import {Label} from 'shells/form/label';
import {FormattedNumberField} from 'shells/formatted-number-field/formatted-number-field';
import {Stack} from 'shells/layout/stack/stack';
import {Radio} from 'shells/radio/radio';
import {RadioGroup} from 'shells/radio/radio-group';
import {SelectPickerGeneric} from 'shells/select-picker-generic/select-picker-generic';
import {Body} from 'shells/typography/body';
import {DescriptionBody} from 'shells/typography/description-body';

import type {Comparator} from 'nutshell-graphql-types';

import {CollectionPicker} from '../../forms/fields/collection-picker';
import {ComparitorSelector} from '../../forms/fields/comparitor-selector';
import {getCollectionNameFromFieldName} from '../../forms/user-assignments/get-collection-name-from-field-name';

import {FIELDS} from './constants';
import type {LeadDistributionRule} from './constants';

import css from './lead-distribution-form.module.css';

import form from '../../forms/sales-process-form.module.css';
import subform from '../../forms/user-assignments/subform-special-case.module.css';

// @ts-expect-error
const trackJs = window.trackJs;

type Option = {
    value: string;
    label: string;
};

type Props = {
    isLoading: boolean;
    schema: Schema.Properties;
    rule: LeadDistributionRule;
    updateRule: (arg1: LeadDistributionRule) => any;
};

export class LeadDistributionForm extends React.PureComponent<Props> {
    options: Option[];

    constructor(props: Props) {
        super(props);
        // @ts-expect-error
        this.options = createOptions(FIELDS, props.schema);
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        // Just in case the schemas weren't fully loaded when the component first
        // mounted, update our options based on the new schema
        if (nextProps.schema !== this.props.schema) {
            if (trackJs) {
                trackJs.track('SubformSpecialCase: schema changed after component was mounted.');
            }

            // @ts-expect-error
            this.options = createOptions(FIELDS, nextProps.schema);
        }
    }

    render() {
        const collectionName =
            this.props.rule.criteriaField === 'value'
                ? null
                : getCollectionNameFromFieldName(this.props.rule.criteriaField, this.props.schema);

        return (
            <div className={subform['special-cases']}>
                <p className={css.description}>
                    Select a rule from the list below to automatically assign your incoming leads to
                    this pipeline.
                </p>
                <Label htmlFor='LeadDistributionCriteriaPicker'>
                    Assign leads to this pipeline when
                </Label>
                <SelectPickerGeneric
                    inputId='LeadDistributionCriteriaPicker'
                    placeholder='- criteria -'
                    isDisabled={this.props.isLoading}
                    value={this.options.filter(
                        ({value}) => value === this.props.rule.criteriaField
                    )}
                    onChange={this.handleCriteriaChange}
                    options={this.options}
                />
                {this.props.rule.criteriaField === 'value' ? (
                    <div className={form['nested-label']}>
                        <ComparitorSelector
                            comparitorValue={this.props.rule.valueComparator}
                            onChange={this.handleValueComparatorChange}
                        />
                        <FormattedNumberField
                            value={Number(this.props.rule.criteriaValues)}
                            onChange={this.handleValueChange}
                        />
                    </div>
                ) : this.props.rule.criteriaField === 'emailDomain' ? (
                    <FormField innerId='emailDomainFilter-true'>
                        <div className='pt-16'>
                            <RadioGroup
                                name='emailDomainFilter'
                                selectedValue={this.props.rule.criteriaValues[0] || null}
                                onChange={this.handleEmailDomainChange}
                            >
                                <Radio value='true' shouldTopAlignInput>
                                    <Stack spacing={4}>
                                        <Body isBold>Is from a common free service</Body>
                                        <DescriptionBody as='span'>
                                            e.g. gmail.com, yahoo.com, hotmail.com, outlook.com,
                                            aol.com
                                        </DescriptionBody>
                                    </Stack>
                                </Radio>
                                <Radio value='false' shouldTopAlignInput>
                                    <Stack spacing={4}>
                                        <Body isBold>Is not from a common free service</Body>
                                        <DescriptionBody as='span'>
                                            e.g. acme.com, company.org (business email domains)
                                        </DescriptionBody>
                                    </Stack>
                                </Radio>
                            </RadioGroup>
                        </div>
                    </FormField>
                ) : (
                    <div>
                        {/* @ts-expect-error */}
                        {this.props.rule.criteriaValues.concat(null).map((value, index) => {
                            return (
                                <div key={value} className={form['value-container']}>
                                    <Label htmlFor='SpecialCaseCriteriaValueType'>
                                        {index === 0 ? 'is' : 'or'}
                                    </Label>
                                    <CollectionPicker
                                        value={value}
                                        entityType='leads'
                                        isDisabled={!collectionName || this.props.isLoading}
                                        collectionType={collectionName}
                                        // @ts-expect-error
                                        onChange={(option) =>
                                            this.handleCriteriaValueChange(option, index)
                                        }
                                        onClear={() => this.handleCriteriaValueClear(index)}
                                        filterOption={this.filterOption}
                                        inputId='SpecialCaseCriteriaValueType'
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    handleCriteriaChange = (option?: Option | null) => {
        const fieldName = option && option.value;

        this.props.updateRule({
            ...this.props.rule,
            criteriaField: fieldName,
            valueComparator: fieldName === 'value' ? 'GREATER' : 'EQUAL',
            criteriaValues: [],
        });
    };

    handleCriteriaValueChange = (option: Option, index: number) => {
        let values = this.props.rule.criteriaValues;
        if (index < values.length) {
            // The user selected an option at an existing index, set it in the array
            values[index] = option.value;
        } else {
            // User set a value outside of the array. This _must_ be at the
            // end of the array, or theres some serious problems
            values = values.concat(option.value);
        }

        this.props.updateRule({
            ...this.props.rule,
            criteriaValues: values,
        });
    };

    handleCriteriaValueClear = (index: number) => {
        const values = this.props.rule.criteriaValues.filter((unused, i) => i !== index);

        this.props.updateRule({
            ...this.props.rule,
            criteriaValues: values,
        });
    };

    handleValueComparatorChange = (option: {value: Comparator}) => {
        this.props.updateRule({
            ...this.props.rule,
            valueComparator: option && option.value,
        });
    };

    handleEmailDomainChange = (value: string) => {
        this.props.updateRule({
            ...this.props.rule,
            criteriaValues: [value],
        });
    };

    handleValueChange = (value?: number | null) => {
        this.props.updateRule({
            ...this.props.rule,
            criteriaValues: [String(value)],
        });
    };

    filterOption = (option: Option) => !_.contains(this.props.rule.criteriaValues, option.value);

    isValid() {
        return Boolean(this.props.rule.criteriaField && this.props.rule.criteriaValues);
    }
}

/*
 * Given a list of options which are fieldnames, and a schema, return
 * a list of options that we can render in a picker list.
 */
const CUSTOM_LABELS: Record<string, string> = {
    emailDomain: 'Email domain',
};

// @ts-expect-error
function createOptions(optionList: string[], schema) {
    return optionList.map((type) => {
        if (CUSTOM_LABELS[type]) {
            return {value: type, label: CUSTOM_LABELS[type]};
        }

        // If we haven't loaded the schema yet, just use the ugly names
        if (!schema[type]) {
            return {value: type, label: type};
        }

        return {value: type, label: schema[type].title};
    });
}
